import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ContainerAvatar as Avatar } from '@atlaskit/navigation-system';
import { useProjectsAnalyticsL2 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { ProjectActionMenu } from '@atlassian/jira-navigation-project-action-menu/src/ui/index.tsx';
import type { JiraLinkConfig } from '@atlassian/jira-router-components/src/ui/router-link-config/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useProjectMenuItemDragAndDrop } from '@atlassian/jira-navigation-apps-sidebar-nav4-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { JiraBusinessProjectMenu$key } from './__generated__/JiraBusinessProjectMenu.graphql';

type JiraBusinessProjectMenuProps = {
	projectData: JiraBusinessProjectMenu$key;
	isCorePremiumUserSeat?: boolean;
	menuItemAnchorRef?: React.RefObject<HTMLAnchorElement>;
};

export const JiraBusinessProjectMenu = ({
	projectData,
	isCorePremiumUserSeat = false,
	menuItemAnchorRef,
}: JiraBusinessProjectMenuProps): React.JSX.Element => {
	const { sendMeatBallDropDownOnProjectsAnalyticsEvent } = useProjectsAnalyticsL2();
	const project = useFragment<JiraBusinessProjectMenu$key>(
		graphql`
			fragment JiraBusinessProjectMenu on JiraProject {
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				projectType @required(action: THROW)
				isFavourite
				defaultNavigationItem {
					... on JiraNavigationItem {
						url
					}
				}
			}
		`,
		projectData,
	);

	let isTargetSameAsCurrentLocation = false;
	if (typeof window !== 'undefined') {
		isTargetSameAsCurrentLocation = window.location.pathname === project.defaultNavigationItem?.url;
	}

	const routerLinkConfig: JiraLinkConfig = useMemo(
		() => ({
			to: project.defaultNavigationItem?.url ?? `/browse/${project.projectKey}`,
			prefetch: isTargetSameAsCurrentLocation ? false : 'hover',
		}),
		[isTargetSameAsCurrentLocation, project.defaultNavigationItem?.url, project.projectKey],
	);

	const projectActionMenuOnClickHandler = useCallback(() => {
		const isFavourite = project.isFavourite;
		const projectId = project.projectId;
		const projectType = project.projectType;

		sendMeatBallDropDownOnProjectsAnalyticsEvent({
			// TODO BLU-5544 send section via <ContextualAnalyticsData instead (which wraps the Starred or Recent panel; and each project). Use static object or useMemo, not to have rerendering issues. Then you can also use static object for meatBallDropDownOnProjectsAnalytics. See Jsm and Wanjel components
			starred: isFavourite ?? false,
			recent: !isFavourite,
			projectId,
			projectType,
		});
	}, [
		project.isFavourite,
		project.projectId,
		project.projectType,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
	]);

	const { projectId, projectKey, isFavourite, name, avatar } = project;
	const { state, draggableAnchorRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId,
			projectKey,
			isFavourite,
			name,
			avatar,
			isEnabled:
				!!isFavourite &&
				expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
		});

	return (
		<>
			<Nav4MenuLinkItem<JiraLinkConfig>
				ref={mergeRefs(menuItemAnchorRef || null, draggableAnchorRef)}
				visualContentRef={dropTargetRef}
				dropIndicator={dropIndicator}
				isDragging={state.type === 'dragging'}
				hasDragIndicator={hasDragIndicator}
				elemBefore={<Avatar src={project.avatar.large || ''} />}
				href={routerLinkConfig}
				menuId={ENTITY_ID.PROJECT(project.projectKey)}
				actionsOnHover={
					<ProjectActionMenu
						isProjectMenuReorderingEnabled={
							Boolean(project.isFavourite) &&
							expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false)
						}
						projectKey={project.projectKey}
						isCorePremiumUserSeat={isCorePremiumUserSeat}
						onClick={projectActionMenuOnClickHandler}
					/>
				}
			>
				{project.name}
			</Nav4MenuLinkItem>
			{dragPreview}
		</>
	);
};
